import currentUser from 'system/currentUser';

/**
 * Namespace needs to be unique to the page and user, because it is a key in localStorage.
 */
const NAMESPACE = 'documents';

function getNameSpace() {
  return `${NAMESPACE}-${currentUser.getUserId()}`;
}

/**
 * @param {object} newValue - represents the whole storage object for this module
 */
function setNameSpacedStorage(newValue) {
  window.localStorage.setItem(getNameSpace(), JSON.stringify(newValue));
}

/**
 * @return {object} Represents all the localStorage for this module
 */
function getNamespacedStorage() {
  const storage = window.localStorage.getItem(getNameSpace());
  let parsedStorage;
  try {
    parsedStorage = window.JSON.parse(storage);
  } catch (e) {
    /* Reset localStorage for this module if it has somehow ended up with a non-json value */
    // eslint-disable-next-line no-console
    console.warn('Invalid JSON in locale storage. Resetting.');
    setNameSpacedStorage({});
  }
  return parsedStorage || {};
}

/**
 * IIFE that clears items from localStorage that have gone past their expiry time
 */
function clearExpiredStorage() {
  const storage = getNamespacedStorage();
  Object.keys(storage).forEach((key) => {
    if (storage[key].expiryTime < Date.now()) {
      delete storage[key];
    }
  });
  setNameSpacedStorage(storage);
}

export default {
  /**
   * Sets a key, within this module's namespace, in localStorage
   * @param {string} key
   * @param {*} value
   * @param {number} daysUntilExpiry - if no value is passed in, it will be cleared at the start of the next session
   * @param {number} daysUntilExpiry - if bool 'true' is passed in instead of a number, it will be set to 99 years
   */
  set(key, value, daysUntilExpiry) {
    const days = daysUntilExpiry === true ? 365 * 99 : daysUntilExpiry; // NOSONAR
    const storage = getNamespacedStorage();
    storage[key] = {
      expiryTime: daysUntilExpiry
        ? Date.now() + (1000 * 60 * 60 * 24 * days)
        : Date.now(),
      value
    };
    setNameSpacedStorage(storage);
  },

  /**
   * Fetches a value from this modules's namespaced localStorage
   * @param {string} key - the key to get from localStorage
   * @return {*} The contents of the key
   */
  get(key) {
    return getNamespacedStorage()[key]?.value;
  },

  /**
   * Fetches the entire localStorage object for this module
   * @return {object}
   */
  getAll() {
    const storage = getNamespacedStorage();
    return Object.keys(storage).reduce((acc, key) => {
      acc[key] = storage[key];
      return acc;
    }, {});
  },

  /**
   * Removes a key from localStorage
   * @param {string} key - the key to unset in localStorage
   */
  unset(key) {
    const storage = getNamespacedStorage();
    delete storage[key];
    setNameSpacedStorage(storage);
  },

  clearExpiredStorage
};
